/* eslint-disable max-lines-per-function */
import { LocalDate } from '@js-joda/core';
import { useMemo } from 'react';

import { FiscalDocument } from '@dreryk/egabinet-invoice-frontend-api';
import { TaxIdentifier } from '@dreryk/tax-identifier-common-api';

import { Modal, ModalCancelButton, ModalCloseButton, ModalConfirmButton, ModalWidth } from '@dreryk/react-components';

import { invoiceCreateModalTranslationKeys } from '../translations';
import { getInvoicePaymentMethod } from '../shared/getInvoicePaymentMethod';

import { CompanyInvoiceCreateModalConfirmButton } from './CompanyInvoiceCreateModalConfirmButton';
import { InvoiceCreateForm } from './form/InvoiceCreateForm';
import { InvoiceCreateFormLoader } from './form/InvoiceCreateForm.loader';
import { fiscalDocumentTypeVisitor } from './form/InvoiceCreateForm.utils';
import { InvoiceCreateFormSwitch } from './form/InvoiceCreateFormSwitch';
import { invoiceCreateFormInvoiceForVisitor } from './form/InvoiceCreateFormSwitch.utils';
import { PrivateIndividualInvoiceCreateModalConfirmButton } from './PrivateIndividualInvoiceCreateModalConfirmButton';

type InvoiceCreateModalProps = Readonly<{
   fiscalDocuments: FiscalDocument[] | undefined;
   fiscalPrintEnabled: boolean;
   patientId: number;
   visitDate: LocalDate;
   appointmentId: number;
   visitSettlementId: number;
   onClose: VoidFunction;
   taxIdentifier?: TaxIdentifier;
}>;

export const InvoiceCreateModal = (props: InvoiceCreateModalProps): JSX.Element => {
   const {
      fiscalDocuments,
      fiscalPrintEnabled,
      onClose,
      patientId,
      taxIdentifier,
      visitDate,
      appointmentId,
      visitSettlementId,
   } = props;

   const receiptAvailableForInvoice = useMemo(
      () =>
         fiscalPrintEnabled &&
         fiscalDocumentTypeVisitor(fiscalDocuments)({
            receiptWithTaxIdentifier: () => true,
            receiptWithoutTaxIdentifier: () => true,
            noFiscalDocuments: () => false,
         }),
      [fiscalDocuments, fiscalPrintEnabled]
   );

   return (
      <Modal
         testId={'invoiceCreateModal'}
         title={
            receiptAvailableForInvoice
               ? invoiceCreateModalTranslationKeys.titleWithReceipt
               : invoiceCreateModalTranslationKeys.title
         }
         width={ModalWidth.HUGE}
      >
         <InvoiceCreateFormLoader patientId={patientId} appointmentId={appointmentId}>
            {({ patient, taxRates, visitSettlement }) =>
               patient && taxRates && visitSettlement && fiscalDocuments ? (
                  <InvoiceCreateFormSwitch fiscalDocuments={fiscalDocuments} taxIdentifier={taxIdentifier}>
                     {({ invoiceFor }) => (
                        <InvoiceCreateForm
                           invoiceFor={invoiceFor}
                           patient={patient}
                           taxRates={taxRates}
                           visitDate={visitDate}
                           visitSettlement={visitSettlement}
                           fiscalDocuments={fiscalDocuments}
                           fiscalPrintEnabled={fiscalPrintEnabled}
                           taxIdentifier={taxIdentifier}
                        >
                           {({ formState, handleSubmit }) => (
                              <>
                                 {invoiceCreateFormInvoiceForVisitor(invoiceFor)({
                                    visitCompany: () => (
                                       <CompanyInvoiceCreateModalConfirmButton
                                          handleSubmit={handleSubmit}
                                          onClose={onClose}
                                          paymentMethod={getInvoicePaymentMethod(visitSettlement.paymentMethod)}
                                          visitDate={visitDate}
                                          visitSettlementId={visitSettlementId}
                                          receiptAvailableForInvoice={receiptAvailableForInvoice}
                                       />
                                    ),
                                    visitPrivateIndividual: () => (
                                       <PrivateIndividualInvoiceCreateModalConfirmButton
                                          handleSubmit={handleSubmit}
                                          onClose={onClose}
                                          paymentMethod={getInvoicePaymentMethod(visitSettlement.paymentMethod)}
                                          visitDate={visitDate}
                                          visitSettlementId={visitSettlementId}
                                          fiscalDocuments={fiscalDocuments}
                                          fiscalPrintEnabled={fiscalPrintEnabled}
                                       />
                                    ),
                                 })}
                                 <ModalCancelButton onClick={onClose} shouldConfirmExit={formState.isDirty} />
                                 <ModalCloseButton onClick={onClose} shouldConfirmExit={formState.isDirty} />
                              </>
                           )}
                        </InvoiceCreateForm>
                     )}
                  </InvoiceCreateFormSwitch>
               ) : (
                  <>
                     <ModalConfirmButton label={invoiceCreateModalTranslationKeys.close} onClick={onClose} />
                     <ModalCloseButton onClick={onClose} />
                  </>
               )
            }
         </InvoiceCreateFormLoader>
      </Modal>
   );
};
